import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TranscriptPage = () => {
  const { studentId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/grades/students/${studentId}/full`, {
        headers: getAuthHeader()
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load transcript');
      navigate('/grades');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-8">
        <Skeleton className="h-10 w-48 mb-4" />
        <Skeleton className="h-6 w-32 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white p-8 text-center">
        <h1 className="text-2xl font-medium text-gray-900 mb-4">Student not found</h1>
        <Button onClick={() => navigate('/grades')}>Back to Grades</Button>
      </div>
    );
  }

  const { student, subjects, overall_gpa } = data;
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Calculate total credits (each subject = 1 credit for simplicity)
  const totalCredits = subjects.filter(s => s.letter_grade !== 'N/A').length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Controls - hidden during print */}
      <div className="print:hidden bg-white border-b p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => navigate(`/grades/${studentId}`)}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Grades
          </Button>
          <Button onClick={handlePrint} className="gap-2" data-testid="print-transcript">
            <Printer className="w-4 h-4" />
            Print Transcript
          </Button>
        </div>
      </div>

      {/* Transcript Content */}
      <div className="max-w-4xl mx-auto p-8 print:p-0 print:max-w-none">
        <div className="bg-white shadow-lg print:shadow-none p-8 print:p-12">
          {/* Header */}
          <div className="text-center mb-8 border-b-2 border-gray-800 pb-6">
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-1">
              HomeClass Academy
            </h1>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Official Transcript</h2>
            <p className="text-sm text-gray-500 italic">
              This document is an official record of academic achievement
            </p>
          </div>

          {/* Student Info */}
          <div className="grid grid-cols-2 gap-8 mb-8 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500 mb-1">Student Name</p>
              <p className="text-lg font-semibold">
                {student.first_name} {student.last_name}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Grade Level</p>
              <p className="text-lg font-semibold">{student.grade_level}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Date Issued</p>
              <p className="text-lg font-semibold">{currentDate}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Cumulative GPA</p>
              <p className="text-lg font-semibold">{overall_gpa.toFixed(2)}</p>
            </div>
          </div>

          {/* Academic Record */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 border-b pb-2">Academic Record</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">
                    Course
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-28">
                    Final Average
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-28">
                    Letter Grade
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-24">
                    GPA Points
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-20">
                    Credits
                  </th>
                </tr>
              </thead>
              <tbody>
                {subjects.length > 0 ? (
                  subjects.map((subject, index) => (
                    <tr key={subject.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-4 py-3 font-medium">
                        {subject.subject_name}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center">
                        {subject.average.toFixed(1)}%
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center font-bold">
                        {subject.letter_grade}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center">
                        {subject.gpa.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center">
                        {subject.letter_grade !== 'N/A' ? '1.0' : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                      No courses recorded
                    </td>
                  </tr>
                )}
              </tbody>
              {subjects.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 font-semibold">
                    <td colSpan={3} className="border border-gray-300 px-4 py-3 text-right">
                      Totals:
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {overall_gpa.toFixed(2)}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {totalCredits.toFixed(1)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Official Notice */}
          <div className="border-t-2 border-gray-800 pt-8 mt-8">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="border-b border-gray-400 mb-2 h-12"></div>
                <p className="text-sm text-gray-500">Authorized Signature</p>
              </div>
              <div>
                <div className="border-b border-gray-400 mb-2 h-12"></div>
                <p className="text-sm text-gray-500">Date</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:p-12 {
            padding: 3rem !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TranscriptPage;
